<template>
    <div>
        <PageHeader>
            <h2>Utenti iscritti</h2>
            <div style="display: flex; align-items: center;">
                <el-button type="success" @click="showDialog = true;">
                    <font-awesome-icon icon="building-circle-arrow-right" style="margin-right: 8px;" />
                    <b>Invita utente</b>
                </el-button>
                <el-button v-if="store.admin.role === 'SUPER'" type="danger" @click="resetTerms">
                    <font-awesome-icon icon="ban" style="margin-right: 8px;" />
                    <b>Azzera termini di servizio</b>
                </el-button>
            </div>
        </PageHeader>

        <el-table :data="items.items" stripe border style="width: 100%" v-loading="isBusy">
            <el-table-column label="Codice fiscale">
                <template #default="scope">
                    <b>{{ scope.row.fiscalCode }}</b>
                </template>
            </el-table-column>
            <el-table-column prop="phoneNumber" label="Numero di telefono" />
            <el-table-column label="Attivo">
                <template #default="scope">
                    <el-switch v-model="scope.row.isActive" disabled></el-switch>
                </template>
            </el-table-column>
            <el-table-column label="Termini e condizioni">
                <template #default="scope">
                    <el-switch v-model="scope.row.termsAndConditions" disabled></el-switch>
                </template>
            </el-table-column>
            <el-table-column v-if="store.admin.role === 'SUPER'" label="Strutture associate" width="170" align="center">
                <template #default="scope">
                    <el-tag>{{ scope.row.organizations.length }}</el-tag>
                    <el-button style="margin-left: 8px;" size="small" @click="showOrganizationsDetails(scope.row)">
                        Dettagli
                    </el-button>
                </template>
            </el-table-column>
            <el-table-column label="Data iscrizione">
                <template #default="scope">
                    {{ getDateString(scope.row.createdAt) }}
                </template>
            </el-table-column>
            <el-table-column v-if="store.admin.role === 'SUPER'" label="Operazioni" width="140" align="center">
                <template #default="scope">
                    <el-button-group>
                        <el-button type="danger" @click="deleteUser(scope.row)">
                            <font-awesome-icon icon="trash" />
                        </el-button>
                    </el-button-group>
                </template>
            </el-table-column>
        </el-table>

        <el-pagination style="margin-top: 10px;" background layout="prev, pager, next" :page-count="items.pages" v-model:current-page="currentPage" @change="onCurrentPageChanged" />

        <el-dialog v-model="showDialog" title="Invita utente" width="800">
            <el-form label-position="top">
                <el-form-item label="Codice fiscale">
                    <el-input class="uppercase" v-model="fiscalCode"></el-input>
                </el-form-item>
                <el-form-item v-if="store.admin.role === 'SUPER'" label="Seleziona una struttura">
                    <el-radio-group v-model="selectedOrganization">
                        <el-radio v-for="organization in organizations" :key="organization.id" :value="organization.organizationId"
                                  border>{{
                                organization.name }}</el-radio>
                    </el-radio-group>
                </el-form-item>
            </el-form>
            <template #footer>
                <div class="dialog-footer">
                    <el-button @click="showDialog = false">Annulla</el-button>
                    <el-button type="success" :loading="isDialogBusy" @click="associateOrganization">
                        Invita
                    </el-button>
                </div>
            </template>
        </el-dialog>

        <el-dialog v-if="selectedUser !== null && showOrganizationsDialog" v-model="showOrganizationsDialog" width="500"
                   title="Strutture associate">
            <el-table :data="selectedUser.organizations" stripe border style="width: 100%" max-height="500">
<!--                <el-table-column>-->
<!--                    <template #default="scope">-->
<!--                    </template>-->
<!--                </el-table-column>-->
                <el-table-column label="Nome">
                    <template #default="scope">
                        <div style="display: flex; align-items: center; column-gap: 10px; justify-content: space-between;">
                            <b>{{ scope.row.name }}</b>
                            <img v-if="scope.row.logo !== null" :src="'data:image/png;base64,' + scope.row.logo" alt=""
                                 style="max-height: 30px;">
                        </div>
                    </template>
                </el-table-column>
            </el-table>
        </el-dialog>

    </div>
</template>

<script lang="ts">
import VueBase from "@/components/VueBase";
import PageHeader from "@/components/PageHeader.vue";
import { Options } from "vue-class-component";
import { ElMessage, ElMessageBox } from "element-plus";
import User from "@/types/User";
import UserService from "@/services/UserService";
import { DateTime } from "luxon";
import Organization from "@/types/Organization";
import OrganizationService from "@/services/OrganizationService";
import {useMainStore} from "@/stores/mainStore";

@Options({
    components: {
        PageHeader
    }
})
export default class UsersView extends VueBase {
    store = useMainStore();
    items: { count: number, pages: number, items: User[] } = { count: 0, pages: 1, items: [] };
    currentPage: number = 1;
    showDialog: boolean = false;
    isDialogBusy: boolean = false;
    organizations: Organization[] = [];
    fiscalCode: string = "";
    selectedOrganization: string | null = this.store.admin!.role === 'SUPER' ? null : this.store.admin!.organization!.organizationId;
    showOrganizationsDialog: boolean = false;
    selectedUser: User | null = null;

    async mounted() {
        await this.getOrganizations();
        await this.getUsers();
    }

    async getUsers() {
        this.isBusy = true;
        try {
            this.items = await UserService.getUsers(this.currentPage, this.store.admin!.role === 'SUPER' ? null : this.store.admin!.organization!.id);
        } catch (error: any) {
            ElMessage.error({ message: error.message });
        }
        this.isBusy = false;
    }

    async resetTerms() {
        ElMessageBox.confirm("Sei sicuro di voler azzerare l'accettazione dei termini di servizio per tutti gli utenti? In questo modo dovranno accettarli nuovamente quando accederanno all'app", {
            title: "Attenzione",
            cancelButtonText: "Annulla",
            confirmButtonText: "Continua"
        })
            .then(async () => {
                this.isBusy = true;
                try {
                    await UserService.resetTermsAndConditions();
                    await this.getUsers();
                } catch (error: any) {
                    ElMessage.error({ message: error.message });
                }
                this.isBusy = false;
            })
            .catch(() => { });
    }

    getDateString(date: string) {
        return DateTime.fromISO(date).toFormat("dd-MM-yy HH:mm");
    }

    async getOrganizations() {
        this.isBusy = true;
        try {
            this.organizations = await OrganizationService.getOrganizations();
        } catch (error: any) {
            ElMessage.error({ message: error.message });
        }
        this.isBusy = false;
    }

    async associateOrganization() {
        try {
            if (this.fiscalCode.trim() === "" || this.selectedOrganization === null)
                throw new Error("Inserire il codice fiscale dell'utente da invitare e selezionare una struttura");
            const link = await UserService.generateInvitation(this.fiscalCode, this.selectedOrganization!);
            this.fiscalCode = "";
            this.selectedOrganization = null;
            this.showDialog = false;
            window.open(link, "_blank");
        } catch (error: any) {
            ElMessage.error({ message: error.message });
        }
    }

    async deleteUser(user: User) {
        ElMessageBox.confirm("Sei sicuro di voler eliminare l'utente selezionato?", {
            title: "Attenzione",
            cancelButtonText: "Annulla",
            confirmButtonText: "Elimina"
        })
            .then(async () => {
                this.isBusy = true;
                try {
                    await UserService.deleteUser(user);
                    await this.getUsers();
                } catch (error: any) {
                    ElMessage.error({ message: error.message });
                }
                this.isBusy = false;
            })
            .catch(() => { });
    }

    async onCurrentPageChanged() {
        await this.getUsers();
    }

    showOrganizationsDetails(user: User) {
        this.selectedUser = user;
        this.showOrganizationsDialog = true;
    }
}
</script>

<style>
.uppercase .el-input__inner {
    text-transform: uppercase;
}
</style>